import { ObjectId, PickSampleExt, rotationFromDirection } from "@novorender/api";
import { mat3, quat, vec2, vec3, vec4 } from "gl-matrix";
import { MouseEventHandler, MutableRefObject, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { featuresConfig } from "config/features";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    HighlightCollection,
    highlightCollectionsActions,
    useDispatchHighlightCollections,
    useHighlightCollections,
} from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { useGoToStation } from "features/alignment/hooks/useGoToStation";
import { alignmentActions } from "features/alignment/slice";
import { USER_SELECTED_ALIGNMENT_NAME } from "features/alignment/utils";
import { useArcgisCanvasClickHandler } from "features/arcgis/hooks/useArcgisCanvasHandler";
import { areaActions } from "features/area";
import { coordinatesActions } from "features/coordinates/coordinatesSlice";
import { useAddCoordinate } from "features/coordinates/hooks/useAddCoordinate";
import { useLocationFormAssetClickHandler } from "features/forms/hooks/useLocationFormAssetClickHandler";
import { usePlaceLocationForm } from "features/forms/hooks/usePlaceLocationForm";
import { heightProfileActions } from "features/heightProfile";
import { jiraActions } from "features/jira";
import { manholeActions } from "features/manhole";
import { markersActions, selectAllCollections, selectSelectedMarker } from "features/markers";
import { MarkerCollection } from "features/markers/types";
import { measureActions, selectMeasure, selectMeasureHoverSettings, selectMeasurePickSettings } from "features/measure";
import { orthoCamActions, selectCrossSectionClipping, selectCrossSectionPoint } from "features/orthoCam";
import { useAddLaser } from "features/outlineLaser/hooks/useAddLaser";
import { clippingOutlineLaserActions } from "features/outlineLaser/outlineLaserSlice";
import { useAddPointToPointLine } from "features/pointLine/hooks/useAddPointToPointLine";
import { selectShowPropertiesStamp } from "features/properties/slice";
import { useSectionPlaneUndoRedo } from "features/sectionPlane/sectionPlaneUndoRedo";
import { undoRedoActions } from "features/undoRedo";
import { useAbortController } from "hooks/useAbortController";
import useFlyTo from "hooks/useFlyTo";
import { useIsMobileMeasureInteraction } from "hooks/useIsMobileMeasureInteraction";
import { useOpenWidget } from "hooks/useOpenWidget";
import { useSuccessiveClickCount } from "hooks/useSuccessiveClickCount";
import {
    selectIsProcoreBeta,
    selectIsProcoreDesign,
    selectNewDesign,
    selectSelectionResolution,
} from "slices/explorer";
import { telemetry } from "telemetry";
import { ExtendedMeasureEntity, NodeType, ViewMode } from "types/misc";
import { isRealVec, measureObjectIsVertex } from "utils/misc";
import {
    extractObjectIds,
    getFilePathFromObjectPath,
    getObjectMetadataRotation,
    isTerrainObjectId,
} from "utils/objectData";
import { searchByPatterns, searchDeepByPatterns } from "utils/search";
import { resolveSelectionTarget } from "utils/selectionResolution";

import {
    renderActions,
    selectCamera,
    selectCameraType,
    selectCanvasSelectionEnabled,
    selectClippingPlanes,
    selectCurrentPickerGeneratedParametricData,
    selectMainObject,
    selectPicker,
    selectSecondaryHighlightProperty,
    selectSelectMultiple,
    selectTerrain,
    selectViewMode,
} from "../renderSlice";
import { CameraType, Picker, StampKind } from "../types";
import { applyCameraDistanceToMeasureTolerance, findCameraAlignedPlane, getLocalRotationAroundNormal } from "../utils";

/**
 * Time window (in milliseconds) after a select box completion during which
 * background clicks are ignored to prevent immediately clearing the selection.
 */
const SELECT_BOX_COMPLETION_GUARD_MS = 200;

/**
 * Helper function to consistently deselect markers with proper callback invocation.
 * Centralizes the deselection logic to ensure all paths behave identically.
 */
function deselectMarkerWithCallback(
    selectedMarker: { collectionId: string; markerId: string } | null,
    markerCollections: Record<string, MarkerCollection>,
    dispatch: ReturnType<typeof useAppDispatch>,
): void {
    if (!selectedMarker) {
        return;
    }

    // Get config from collection to invoke onDeselect callback
    const collection = markerCollections[selectedMarker.collectionId];
    const config = collection?.config;
    if (config?.onDeselect) {
        // Get the marker data to pass to callback
        const marker = collection?.markers[selectedMarker.markerId];
        if (marker) {
            config.onDeselect(marker);
        }
    }

    // Dispatch Redux action to update state
    dispatch(markersActions.deselectMarker());
}

/**
 * Checks if we're within the guard window after a select box completion.
 * Used to prevent clicks/interactions from interfering with select box results.
 *
 * @param lastSelectBoxCompletionTime - Ref to the timestamp when select box last completed
 * @returns true if we're within the guard window, false otherwise
 */
function isWithinSelectBoxGuardWindow(lastSelectBoxCompletionTime: MutableRefObject<number> | undefined): boolean {
    if (!lastSelectBoxCompletionTime) {
        return false;
    }

    const timeSinceSelectBox = Date.now() - lastSelectBoxCompletionTime.current;
    return timeSinceSelectBox < SELECT_BOX_COMPLETION_GUARD_MS;
}

export function useCanvasClickHandler({
    pointerDownStateRef,
    lastSelectBoxCompletionTime,
}: {
    pointerDownStateRef: MutableRefObject<
        | {
              timestamp: number;
              x: number;
              y: number;
          }
        | undefined
    >;
    lastSelectBoxCompletionTime?: MutableRefObject<number>;
}) {
    const dispatch = useAppDispatch();
    const { addClippingPlaneWithUndo } = useSectionPlaneUndoRedo();
    const highlightedObjects = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const highlightCollections = useHighlightCollections();
    const {
        state: { view, canvas, db, size, internalEventEmitter },
    } = useExplorerGlobals();

    const cameraType = useAppSelector(selectCameraType);
    const mainObject = useAppSelector(selectMainObject);
    const selectMultiple = useAppSelector(selectSelectMultiple);
    const cameraState = useAppSelector(selectCamera);
    const measure = useAppSelector(selectMeasure);
    const measureHoverSettings = useAppSelector(selectMeasureHoverSettings);
    const picker = useAppSelector(selectPicker);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const crossSectionPoint = useAppSelector(selectCrossSectionPoint);
    const crossSectionClipping = useAppSelector(selectCrossSectionClipping);
    const viewMode = useAppSelector(selectViewMode);
    const showPropertiesStamp = useAppSelector(selectShowPropertiesStamp);
    const { planes } = useAppSelector(selectClippingPlanes);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);
    const newDesign = useAppSelector(selectNewDesign);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isMobileMeasureInteraction = useIsMobileMeasureInteraction();
    const terrain = useAppSelector(selectTerrain);
    const canvasSelectionEnabled = useAppSelector(selectCanvasSelectionEnabled);
    const selectionResolution = useAppSelector(selectSelectionResolution);
    const selectedMarker = useAppSelector(selectSelectedMarker);
    const markerCollections = useAppSelector(selectAllCollections);
    const isProcoreBeta = useAppSelector(selectIsProcoreBeta);
    const [dblClickZoomAbortControllerRef, abortDblClickZoom] = useAbortController();

    const [secondaryHighlightAbortController, abortSecondaryHighlight] = useAbortController();
    const currentSecondaryHighlightQuery = useRef("");
    const secondaryHighlightProperty = useAppSelector(selectSecondaryHighlightProperty);

    const arcgisCanvasClickHandler = useArcgisCanvasClickHandler();
    const placeLocationForm = usePlaceLocationForm();
    const locationFormAssetClickHandler = useLocationFormAssetClickHandler();
    const openWidget = useOpenWidget();
    const dispatchLastPickSample = useDispatchLastPickSample();

    const addLaser = useAddLaser();
    const addCoordinate = useAddCoordinate();
    const addPointToPointLine = useAddPointToPointLine();

    const goToStation = useGoToStation();
    const flyTo = useFlyTo();
    const clickCountRef = useSuccessiveClickCount();
    const lastClickedObjectIdRef = useRef<ObjectId | null>(null);

    const isEntitySelected = (entity: ExtendedMeasureEntity): boolean => {
        const currentEntities = measure.selectedEntities[measure.currentIndex];
        if (!currentEntities) {
            return false;
        }
        return currentEntities.some(
            (selectedEntity) =>
                selectedEntity.ObjectId === entity.ObjectId &&
                selectedEntity.instanceIndex === entity.instanceIndex &&
                selectedEntity.pathIndex === entity.pathIndex &&
                selectedEntity.drawKind === entity.drawKind,
        );
    };

    const handleAddLaser = (
        position: vec3,
        normal: vec3,
        options: { attachedToPlane?: boolean; measureZ?: boolean; objectId?: ObjectId },
    ) => {
        const laserId = crypto.randomUUID();
        const { attachedToPlane, measureZ, objectId } = options;
        const laserAdded = addLaser(position, normal, {
            laserId,
            measureZ,
            attachedToPlane,
            objectId,
        });
        if (laserAdded) {
            dispatch(
                undoRedoActions.push({
                    description: "Add laser measurement",
                    undoCallback: () => {
                        dispatch(clippingOutlineLaserActions.removeLaser(laserId));
                    },
                    redoCallback: () => {
                        addLaser(position, normal, {
                            laserId,
                            measureZ,
                            attachedToPlane,
                            objectId,
                        });
                    },
                }),
            );
        }
    };

    const handleCanvasPick: MouseEventHandler<HTMLCanvasElement> = async (evt) => {
        const pointerDownState = pointerDownStateRef.current;
        const longPress = pointerDownState && evt.timeStamp - pointerDownState.timestamp >= 800;
        const drag =
            pointerDownState &&
            vec2.dist([pointerDownState.x, pointerDownState.y], [evt.nativeEvent.offsetX, evt.nativeEvent.offsetY]) >=
                5;

        if (!view || !canvas || longPress || drag) {
            return;
        }

        const mobileMeasurePickers = [Picker.Dimension, Picker.PointLine, Picker.OutlineLaser, Picker.Coordinates];
        if (isMobileMeasureInteraction && mobileMeasurePickers.includes(picker)) {
            return;
        }

        if (picker === Picker.Object && arcgisCanvasClickHandler && arcgisCanvasClickHandler(evt)) {
            return;
        }

        const planePicking = cameraType === CameraType.Orthographic && view.renderState.camera.far < 1;

        function unselectDimensionWithUndoRedo(entity: ExtendedMeasureEntity) {
            const entitiesToRestore = structuredClone(measure.selectedEntities[measure.currentIndex] || []);
            dispatch(measureActions.unselectEntity(entity));
            dispatch(
                undoRedoActions.push({
                    description: "Unselect dimension measurement",
                    undoCallback: () => dispatch(measureActions.setSelectedEntities(entitiesToRestore)),
                    redoCallback: () => dispatch(measureActions.unselectEntity(entity)),
                }),
            );
        }

        function addDimensionWithUndoRedo(entity: ExtendedMeasureEntity, shiftKey: boolean) {
            const previousEntities = structuredClone(measure.selectedEntities[measure.currentIndex] || []);
            const currentSet = measure.selectedEntities[measure.currentIndex] || [];
            const isReplace = !shiftKey;
            const isAddingSecond = currentSet.length === 1 && shiftKey;

            if (isReplace) {
                dispatch(measureActions.setSelectedEntities([entity]));
                dispatch(
                    undoRedoActions.push({
                        description: "Add dimension measurement",
                        undoCallback: () => dispatch(measureActions.setSelectedEntities(previousEntities)),
                        redoCallback: () => dispatch(measureActions.setSelectedEntities([entity])),
                    }),
                );
                return;
            }

            if (currentSet.length === 0) {
                dispatch(measureActions.selectEntity({ entity, pin: true }));
            } else {
                if (measure.pinned !== 0) {
                    dispatch(measureActions.pin(0));
                }
                dispatch(measureActions.selectEntity({ entity, pin: false }));
            }
            dispatch(
                undoRedoActions.push({
                    description: "Add dimension measurement",
                    undoCallback: () => dispatch(measureActions.setSelectedEntities(previousEntities)),
                    redoCallback: () => {
                        if (isAddingSecond) {
                            dispatch(measureActions.pin(0));
                        }
                        dispatch(
                            measureActions.selectEntity({
                                entity,
                                pin: isAddingSecond ? false : shiftKey,
                            }),
                        );
                    },
                }),
            );
        }

        pointerDownStateRef.current = undefined;
        const pickCameraPlane =
            cameraState.type === CameraType.Orthographic &&
            (viewMode === ViewMode.CrossSection ||
                viewMode === ViewMode.FollowPath ||
                viewMode === ViewMode.Deviations);

        const isTouch = evt.nativeEvent instanceof PointerEvent && evt.nativeEvent.pointerType === "touch";
        const pickOutline = measure.snapKind === "clippingOutline" && picker === Picker.Measurement;
        const result = await view.pick(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY, {
            pickCameraPlane,
            sampleDiscRadius: isTouch ? 8 : 4,
            pickOutline,
        });

        if (result?.position) {
            internalEventEmitter?.emit("intersectPointClick", result.position);
        } else {
            internalEventEmitter?.emit("intersectPointClick", null);
        }

        // Zoom on object on double click
        {
            // Use lazyLastPickSample instead of mainObject, because if user has object selected prior -
            // first click will unselect it and mainObject will be undefined.
            // Using lastClickedObjectIdRef instead of lazyLastPickSample, because lastPickSample is also updated
            // on mouse move, which we don't want here.
            const clickCount = clickCountRef.registerClick();
            const isSameObjectPicked = result?.objectId && result.objectId === lastClickedObjectIdRef.current;
            if (isSameObjectPicked && picker === Picker.Object && clickCount >= 2) {
                if (clickCount === 2) {
                    // Zoom in on the object on double click, ignore subsequent clicks
                    abortDblClickZoom();
                    flyTo({ objectIds: [result.objectId], abortSignal: dblClickZoomAbortControllerRef.current.signal });
                }

                if (result.objectId === mainObject) {
                    // Return if main object is already selected to prevent unselecting it
                    return;
                }

                if (clickCount > 2) {
                    // Ignore 3+ clicks on the same object to avoid unselecting/selecting the object rapidly
                    return;
                }
            }
        }

        lastClickedObjectIdRef.current = result?.objectId ?? null;

        async function pickMeasureEntity(result: PickSampleExt) {
            if (!view) {
                return;
            }
            const now = performance.now();
            dispatch(renderActions.addLoadingHandle(now));
            try {
                const tolerance = applyCameraDistanceToMeasureTolerance(
                    result.position,
                    view.renderState.camera,
                    measureHoverSettings,
                );
                const hover =
                    view.measure &&
                    (await view.measure.core.pickMeasureEntity(
                        result.objectId,
                        result.position,
                        tolerance,
                        allowGeneratedParametric.enabled,
                    ));
                return (
                    hover?.entity && {
                        ...hover.entity,
                        connectionPoint: hover.connectionPoint,
                    }
                );
            } finally {
                dispatch(renderActions.removeLoadingHandle(now));
            }
        }

        dispatchLastPickSample(lastPickSampleActions.set(result ?? null));
        if (
            !result ||
            picker === Picker.CrossSection ||
            picker === Picker.OutlineLaser ||
            picker === Picker.Coordinates
        ) {
            if (picker === Picker.Object) {
                dispatch(renderActions.setStamp(null));

                if (!result && highlightedObjects.idArr.length > 0) {
                    // Prevent clearing selection if a select box just completed
                    // This prevents the background click from clearing the selection immediately after select box release
                    if (isWithinSelectBoxGuardWindow(lastSelectBoxCompletionTime)) {
                        // Select box just completed, ignore this background click
                        return;
                    }

                    const clearSelection = () => {
                        // Set mainObject to undefined FIRST, then clear selection
                        // This prevents race conditions with components that react to mainObject changes
                        dispatch(renderActions.setMainObject(undefined));
                        dispatchHighlighted(highlightActions.setIds([]));
                    };
                    clearSelection();

                    dispatch(
                        undoRedoActions.push({
                            description: "Clear selection",
                            undoCallback: () => {
                                // When undoing, set selection FIRST, then mainObject
                                dispatchHighlighted(highlightActions.setIds(highlightedObjects.idArr));
                                if (mainObject !== undefined) {
                                    dispatch(renderActions.setMainObject(mainObject));
                                }
                            },
                            redoCallback: clearSelection,
                        }),
                    );
                }

                // Deselect marker when clicking on background (empty space)
                if (!result && selectedMarker) {
                    deselectMarkerWithCallback(selectedMarker, markerCollections, dispatch);
                }

                return;
            } else if (picker === Picker.Measurement) {
                if (measure.hover) {
                    dispatch(
                        measureActions.selectEntity({
                            entity: planePicking
                                ? {
                                      ...measure.hover,
                                      settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                                  }
                                : measure.hover,
                            pin: evt.shiftKey,
                        }),
                    );
                }
                return;
            } else if (picker === Picker.Dimension) {
                if (measure.hover && !measureObjectIsVertex(measure.hover)) {
                    const entity = planePicking
                        ? {
                              ...measure.hover,
                              settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                          }
                        : measure.hover;

                    if (isEntitySelected(entity)) {
                        unselectDimensionWithUndoRedo(entity);
                        return;
                    }

                    addDimensionWithUndoRedo(entity, evt.shiftKey);
                }
                return;
            } else if (picker === Picker.Area) {
                if (measureObjectIsVertex(measure.hover) && planes.length) {
                    dispatch(areaActions.addPt(measure.hover.parameter, view));
                }
                return;
            } else if (picker === Picker.PointLine) {
                if (measureObjectIsVertex(measure.hover)) {
                    addPointToPointLine({
                        position: measure.hover.parameter,
                        position2d: [evt.nativeEvent.offsetX, evt.nativeEvent.offsetY],
                        objectId: measure.hover.ObjectId,
                        measureEntity: measure.hover,
                    });
                }
                return;
            } else if (picker === Picker.CrossSection) {
                const position =
                    result?.position ??
                    view.convert.screenSpaceToWorldSpace([
                        vec2.fromValues(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY),
                    ])[0];

                if (!position) {
                    return;
                }

                if (!crossSectionPoint) {
                    dispatch(orthoCamActions.setCrossSectionPoint(position));
                    return;
                }

                if (newDesign) {
                    openWidget(featuresConfig.crossSection.key);
                }
                const mat = mat3.fromQuat(mat3.create(), view.renderState.camera.rotation);
                const up = vec3.fromValues(0, 0, 1);
                const topDown = vec3.equals(vec3.fromValues(mat[6], mat[7], mat[8]), up);
                const pos = topDown
                    ? vec3.fromValues(position[0], position[1], crossSectionPoint[2])
                    : vec3.copy(vec3.create(), position);

                const right = vec3.sub(vec3.create(), crossSectionPoint, pos);
                const l = vec3.len(right);
                vec3.scale(right, right, 1 / l);
                const p = vec3.scaleAndAdd(vec3.create(), crossSectionPoint, right, l / -2);
                let dir = vec3.cross(vec3.create(), up, right);

                if (topDown) {
                    const midPt = (view.measure?.draw.toMarkerPoints([p]) ?? [])[0];
                    if (midPt && midPt[0] >= 0 && midPt[0] < size.width && midPt[1] >= 0 && midPt[1] < size.height) {
                        // Why doing a pick here? People often put first and second cross section points on opposite sides of the road
                        // and they can be outside the road, so they don't represent relevant elevation.
                        // By doing this pick we try to put the camera at the proper elevation.
                        const midPick = await view.pick(midPt[0], midPt[1], {
                            sampleDiscRadius: 100,
                            searchType: "closestToPickCenter",
                        });
                        if (midPick) {
                            vec3.copy(p, midPick.position);
                        }
                    }
                } else if (right[1] < 0.01) {
                    right[1] = 0;
                    dir = vec3.clone(up);
                    vec3.cross(up, right, dir);
                    vec3.normalize(up, up);
                } else {
                    vec3.normalize(dir, dir);
                }
                vec3.cross(right, up, dir);
                vec3.normalize(right, right);

                if (!newDesign) {
                    const rotation = quat.fromMat3(
                        quat.create(),
                        mat3.fromValues(right[0], right[1], right[2], up[0], up[1], up[2], dir[0], dir[1], dir[2]),
                    );

                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                            goTo: {
                                position: p,
                                rotation,
                                fov: 45,
                                far: crossSectionClipping,
                            },
                            gridOrigo: p,
                        }),
                    );
                }
                const w = vec3.dot(dir, p);
                dispatch(
                    renderActions.setClippingPlanes({
                        enabled: true,
                        planes: [
                            {
                                normalOffset: [dir[0], dir[1], dir[2], w],
                                baseW: w,
                                color: [0, 1, 0, 0.2],
                                anchorPos: p,
                            },
                        ],
                    }),
                );
                dispatch(renderActions.setPicker(Picker.Object));
                dispatch(orthoCamActions.setCrossSectionPoint(undefined));
                dispatch(orthoCamActions.setCrossSectionHover(undefined));
                return;
            } else if (picker === Picker.OutlineLaser) {
                const cameraAlignedPlane = findCameraAlignedPlane(view);
                const isOrtho = cameraType === CameraType.Orthographic;
                if (isOrtho && cameraAlignedPlane && viewMode === ViewMode.CrossSection) {
                    const normal = vec3.fromValues(
                        cameraAlignedPlane.normalOffset[0],
                        cameraAlignedPlane.normalOffset[1],
                        cameraAlignedPlane.normalOffset[2],
                    );
                    const position = view.convert.screenSpaceToWorldSpace([
                        vec2.fromValues(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY),
                    ])[0];
                    if (!position) {
                        return;
                    }
                    handleAddLaser(position, normal, { attachedToPlane: true, measureZ: false });
                } else if (result) {
                    handleAddLaser(result.position, result.normal, {
                        objectId: result.objectId,
                        measureZ: !(isOrtho && cameraAlignedPlane),
                    });
                }
                return;
            } else if (picker === Picker.Coordinates) {
                if (result) {
                    const coordinate = await addCoordinate(result.position, {
                        screenOffset: {
                            x: result.x,
                            y: result.y,
                        },
                        normal: result.normal,
                        coordinateId: crypto.randomUUID(),
                        objectId: result.objectId,
                    });
                    if (coordinate) {
                        dispatch(
                            undoRedoActions.push({
                                description: "Add coordinate",
                                undoCallback: () => dispatch(coordinatesActions.removeCoordinate(coordinate.id)),
                                redoCallback: () => dispatch(coordinatesActions.addCoordinate(coordinate)),
                            }),
                        );
                    }
                }
                return;
            } else {
                return;
            }
        }

        const normal =
            isRealVec([...result.normal]) && vec3.dot(result.normal, result.normal) !== 0
                ? vec3.clone(result.normal)
                : undefined;
        const position = vec3.clone(result.position);

        if (picker === Picker.FormLocation) {
            if (result) {
                placeLocationForm({ location: position });
                dispatch(renderActions.stopPicker(Picker.FormLocation));
            }
        } else if (picker === Picker.JiraIssuePosition) {
            if (result) {
                dispatch(jiraActions.setNewIssuePosition(position));
                dispatch(renderActions.stopPicker(Picker.JiraIssuePosition));
            }
            return;
        } else if (picker === Picker.Object) {
            if (!canvasSelectionEnabled) {
                return;
            }

            if (
                cameraState.type === CameraType.Orthographic &&
                result.pointFactor !== undefined &&
                [ViewMode.CrossSection, ViewMode.FollowPath, ViewMode.Deviations].includes(viewMode)
            ) {
                dispatch(
                    renderActions.setStamp({
                        kind: StampKind.Deviation,
                        mouseX: evt.nativeEvent.offsetX,
                        mouseY: evt.nativeEvent.offsetY,
                        pinned: false,
                        data: {
                            deviation: result.pointFactor,
                        },
                    }),
                );
                return;
            }

            if (locationFormAssetClickHandler(result)) {
                return;
            }

            if (result.objectId === -1 || (!terrain.canSelect && isTerrainObjectId(result.objectId))) {
                dispatch(renderActions.setStamp(null));
                return;
            }

            // Prevent object clicks from interfering with select box completion
            // When select box is released over an object, both finalize and click fire
            // We need to ignore the click to preserve the select box results
            if (isWithinSelectBoxGuardWindow(lastSelectBoxCompletionTime)) {
                // Select box just completed, ignore this object click
                return;
            }

            // Resolve the selection target based on the selection resolution setting
            if (!db) {
                return;
            }
            const selectionTarget = await resolveSelectionTarget({
                objectId: result.objectId,
                db,
                view,
                resolution: selectionResolution,
            });

            const alreadySelected = highlightedObjects.ids[selectionTarget.mainObjectId];

            if (selectMultiple) {
                if (alreadySelected) {
                    const deselectObject = () => {
                        if (selectionTarget.mainObjectId === mainObject) {
                            dispatch(renderActions.setMainObject(undefined));
                        }
                        dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));
                    };
                    deselectObject();

                    dispatch(
                        undoRedoActions.push({
                            description: "Multiselect already selected object",
                            undoCallback: () => {
                                if (selectionTarget.mainObjectId === mainObject) {
                                    dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                                }
                                dispatchHighlighted(highlightActions.add(selectionTarget.highlightIds));
                            },
                            redoCallback: deselectObject,
                        }),
                    );
                } else {
                    const selectObject = () => {
                        dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                        dispatchHighlighted(highlightActions.add(selectionTarget.highlightIds));

                        // Deselect marker when multiselecting objects
                        if (selectedMarker) {
                            deselectMarkerWithCallback(selectedMarker, markerCollections, dispatch);
                        }
                    };
                    selectObject();
                    dispatch(
                        undoRedoActions.push({
                            description: "Multiselect object",
                            undoCallback: () => {
                                dispatch(renderActions.setMainObject(mainObject));
                                dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));
                            },
                            redoCallback: selectObject,
                        }),
                    );
                }
            } else if (alreadySelected && isProcoreDesign) {
                const deselectObject = () => {
                    if (selectionTarget.mainObjectId === mainObject) {
                        dispatch(renderActions.setMainObject(undefined));
                        dispatch(renderActions.setStamp(null));
                    }
                    dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));

                    if (!secondaryHighlightProperty) {
                        return;
                    }

                    abortSecondaryHighlight();
                };
                deselectObject();

                dispatch(
                    undoRedoActions.push({
                        description: "Select already selected object",
                        undoCallback: () => {
                            dispatchHighlighted(highlightActions.add(selectionTarget.highlightIds));
                            dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                        },
                        redoCallback: deselectObject,
                    }),
                );
            } else if (alreadySelected && selectionTarget.mainObjectId === mainObject) {
                dispatch(renderActions.setMainObject(undefined));
                dispatch(renderActions.setStamp(null));
                dispatchHighlighted(highlightActions.remove(selectionTarget.highlightIds));

                if (!secondaryHighlightProperty) {
                    return;
                }

                abortSecondaryHighlight();
                dispatchHighlightCollections(
                    highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, []),
                );
                currentSecondaryHighlightQuery.current = "";
            } else {
                const selectObject = () => {
                    dispatch(renderActions.setMainObject(selectionTarget.mainObjectId));
                    if (!alreadySelected) {
                        dispatchHighlighted(highlightActions.setIds(selectionTarget.highlightIds));
                    } else if (highlightedObjects.idArr.length > 1) {
                        // Clicking already-selected object without modifier: clear all selections
                        dispatch(renderActions.setMainObject(undefined));
                        dispatchHighlighted(highlightActions.setIds([]));
                    }

                    // Deselect marker when selecting a 3D object
                    if (selectedMarker) {
                        deselectMarkerWithCallback(selectedMarker, markerCollections, dispatch);
                    }
                };
                selectObject();
                dispatch(
                    undoRedoActions.push({
                        description: "Select object",
                        undoCallback: () => {
                            dispatch(renderActions.setMainObject(mainObject));
                            dispatchHighlighted(highlightActions.setIds(highlightedObjects.idArr));
                        },
                        redoCallback: selectObject,
                    }),
                );

                if ((!showPropertiesStamp && !secondaryHighlightProperty) || !db) {
                    return;
                }

                const metadata = await (view.data
                    ? view.data.getObjectMetaData(selectionTarget.mainObjectId)
                    : db.getObjectMetdata(selectionTarget.mainObjectId));

                const hierarchyMeta = [metadata];
                try {
                    const filePath = getFilePathFromObjectPath(metadata.path);
                    const subfilePath = filePath ? metadata.path.substring(filePath.length + 1) : metadata.path;
                    const iter = db.search(
                        {
                            full: true,
                            parentPath: metadata.path,
                            descentDepth: -([...subfilePath].reduce((acc, c) => (c === "/" ? acc + 1 : acc), 0) + 1),
                        },
                        undefined,
                    );
                    for await (const obj of iter) {
                        hierarchyMeta.push(await obj.loadMetaData());
                    }
                    hierarchyMeta.sort((a, b) => b.path.length - a.path.length);
                } catch (e) {
                    console.warn("Error getting hierarchy metadata", e);
                    telemetry.trackCaughtException({
                        source: "render.useCanvasClickHandler.hierarchyMetadata",
                        category: "render",
                        error: e,
                    });
                }

                if (showPropertiesStamp && !isProcoreDesign) {
                    dispatch(
                        renderActions.setStamp({
                            kind: StampKind.Properties,
                            objectIds: hierarchyMeta.map((obj) => obj.id),
                            properties: hierarchyMeta.map((obj) => [
                                ["Name", obj.name],
                                ["Path", obj.path],
                                ...obj.properties,
                            ]),
                            mouseX: evt.nativeEvent.offsetX,
                            mouseY: evt.nativeEvent.offsetY,
                            pinned: true,
                        }),
                    );
                }

                if (!secondaryHighlightProperty) {
                    return;
                }

                const property = metadata.properties.find((prop) => prop[0] === secondaryHighlightProperty);
                const query = property?.[1];

                if (
                    query &&
                    query === currentSecondaryHighlightQuery.current &&
                    highlightCollections.secondaryHighlight.idArr.length
                ) {
                    return;
                }

                abortSecondaryHighlight();
                dispatchHighlightCollections(
                    highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, []),
                );

                if (!query) {
                    return;
                }

                const abortSignal = secondaryHighlightAbortController.current.signal;
                const loadingHandle = evt.timeStamp;
                currentSecondaryHighlightQuery.current = query;
                dispatch(renderActions.addLoadingHandle(loadingHandle));

                try {
                    const res: number[] = [];

                    const baseSearchProps = {
                        db,
                        abortSignal,
                        searchPatterns: [{ property: secondaryHighlightProperty, value: query, exact: true }],
                    };

                    if (metadata.type === NodeType.Internal) {
                        await searchDeepByPatterns({
                            ...baseSearchProps,
                            callback: (ids) => res.push(...ids),
                        });
                    } else {
                        await searchByPatterns({
                            ...baseSearchProps,
                            callback: (refs) => res.push(...extractObjectIds(refs)),
                        });
                    }

                    dispatchHighlightCollections(
                        highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, res),
                    );
                } catch (e) {
                    if (!abortSignal.aborted) {
                        console.warn(e);
                        telemetry.trackCaughtException({
                            source: "render.useCanvasClickHandler.secondaryHighlight",
                            category: "render",
                            error: e,
                        });
                    }
                } finally {
                    dispatch(renderActions.removeLoadingHandle(loadingHandle));
                }
            }
        } else if (picker === Picker.ClippingPlane) {
            if (!normal || result.sampleType !== "surface" || !db) {
                return;
            }

            let rotation = 0;
            try {
                if (result.objectId) {
                    const rotationQuat = await getObjectMetadataRotation(view, db, result.objectId);
                    if (rotationQuat) {
                        rotation = getLocalRotationAroundNormal(rotationQuat, normal);
                    }
                }
            } catch (ex) {
                console.warn("Error getting object rotation", ex);
                telemetry.trackCaughtException({
                    source: "render.useCanvasClickHandler.clipRotation",
                    category: "render",
                    error: ex,
                });
            }

            const w = vec3.dot(normal, position);

            if (isProcoreBeta) {
                dispatch(renderActions.setSectionPlaneHoverObjectId(null));
            }
            dispatch(renderActions.setPicker(Picker.Object));
            dispatch(
                renderActions.addClippingPlane({
                    normalOffset: vec4.fromValues(normal[0], normal[1], normal[2], w) as Vec4,
                    baseW: w,
                    rotation,
                    anchorPos: result?.position,
                    showPlane: false,
                }),
            );
        } else if (picker === Picker.SectionPlane) {
            if (!normal || result.sampleType !== "surface") {
                return;
            }

            let rotation = 0;
            try {
                if (result.objectId && db) {
                    const rotationQuat = await getObjectMetadataRotation(view, db, result.objectId);
                    if (rotationQuat) {
                        rotation = getLocalRotationAroundNormal(rotationQuat, normal);
                    }
                }
            } catch (ex) {
                console.warn("Error getting object rotation", ex);
                telemetry.trackCaughtException({
                    source: "render.useCanvasClickHandler.sectionPlaneRotation",
                    category: "render",
                    error: ex,
                });
            }

            const w = vec3.dot(normal, position);

            dispatch(renderActions.setSectionPlaneHoverObjectId(null));
            dispatch(renderActions.setPicker(Picker.Object));
            addClippingPlaneWithUndo(
                {
                    normalOffset: vec4.fromValues(normal[0], normal[1], normal[2], w),
                    baseW: w,
                    rotation,
                    anchorPos: result?.position,
                    showPlane: false,
                },
                {
                    screenPoint: vec2.fromValues(evt.nativeEvent.offsetX, evt.nativeEvent.offsetY),
                },
            );
        } else if (picker === Picker.OrthoPlane) {
            if (!normal || result.sampleType !== "surface") {
                return;
            }

            dispatch(
                renderActions.setCamera({
                    type: CameraType.Orthographic,
                    goTo: {
                        position,
                        rotation: rotationFromDirection(normal),
                        fov: 50,
                    },
                }),
            );
            dispatch(renderActions.setPicker(Picker.Object));
        } else if (picker === Picker.Measurement) {
            if (measure.hover) {
                dispatch(
                    measureActions.selectEntity({
                        entity: planePicking
                            ? {
                                  ...measure.hover,
                                  settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                              }
                            : measure.hover,
                        pin: evt.shiftKey,
                    }),
                );
            } else if (measure.snapKind === "clippingOutline") {
                const pointEntity: ExtendedMeasureEntity = {
                    drawKind: "vertex",
                    ObjectId: result.objectId,
                    parameter: result.position,
                };
                dispatch(
                    measureActions.selectEntity({
                        entity: pointEntity,
                        pin: evt.shiftKey,
                    }),
                );
            } else {
                dispatch(measureActions.setLoadingBrep(true));
                const tolerance = applyCameraDistanceToMeasureTolerance(
                    result.position,
                    view.renderState.camera,
                    measurePickSettings,
                );
                const entity = await view.measure?.core.pickMeasureEntity(
                    result.objectId,
                    position,
                    tolerance,
                    allowGeneratedParametric.enabled,
                );
                if (entity?.entity) {
                    dispatch(
                        measureActions.selectEntity({
                            entity: entity.entity,
                            pin: evt.shiftKey,
                        }),
                    );
                }
                dispatch(measureActions.setLoadingBrep(false));
            }
        } else if (picker === Picker.Dimension) {
            if (measure.hover && !measureObjectIsVertex(measure.hover)) {
                const entity = planePicking
                    ? {
                          ...measure.hover,
                          settings: { planeMeasure: view.renderState.clipping.planes[0]?.normalOffset },
                      }
                    : measure.hover;

                if (isEntitySelected(entity)) {
                    unselectDimensionWithUndoRedo(entity);
                    return;
                }

                addDimensionWithUndoRedo(entity, evt.shiftKey);
            } else if (measure.snapKind === "clippingOutline") {
                const pointEntity: ExtendedMeasureEntity = {
                    drawKind: "vertex",
                    ObjectId: result.objectId,
                    parameter: result.position,
                };

                if (isEntitySelected(pointEntity)) {
                    unselectDimensionWithUndoRedo(pointEntity);
                    return;
                }

                addDimensionWithUndoRedo(pointEntity, evt.shiftKey);
            } else {
                dispatch(measureActions.setLoadingBrep(true));
                const tolerance = applyCameraDistanceToMeasureTolerance(
                    result.position,
                    view.renderState.camera,
                    measurePickSettings,
                );
                const entity = await view.measure?.core.pickMeasureEntity(
                    result.objectId,
                    position,
                    tolerance,
                    allowGeneratedParametric.enabled,
                );
                if (entity?.entity && !measureObjectIsVertex(entity.entity)) {
                    if (isEntitySelected(entity?.entity)) {
                        unselectDimensionWithUndoRedo(entity.entity as ExtendedMeasureEntity);
                        return;
                    }

                    addDimensionWithUndoRedo(entity.entity as ExtendedMeasureEntity, evt.shiftKey);
                }
                dispatch(measureActions.setLoadingBrep(false));
            }
        } else if (picker === Picker.Manhole) {
            if (result.objectId === -1) {
                return;
            }
            dispatch(manholeActions.selectObj({ id: result.objectId, pos: position }));
        } else if (picker === Picker.FollowPathObject) {
            if (result.objectId === -1 || !view.measure) {
                return;
            }

            // User may either select an alignment or a random curve without an alignment
            // Alignment will have (alignment, fpObj, stationInfo)
            // Random curve - only fpObj
            const fpObj = await view.measure.followPath.followParametricObjects([result.objectId]);
            if (!fpObj) {
                return;
            }

            const alignment = await view.measure?.road.getAlignment(result.objectId);
            const stationInfo = alignment
                ? view.measure.road.getStationInfoAtPoint(alignment, result.position)
                : undefined;

            dispatch(renderActions.stopPicker(Picker.FollowPathObject));
            dispatch(alignmentActions.setFollowObject(fpObj));
            dispatch(alignmentActions.setAlignment({ id: result.objectId, name: USER_SELECTED_ALIGNMENT_NAME }));
            goToStation({
                fpObj,
                station: stationInfo?.station ?? view.measure.followPath.getClosestStation(fpObj, result.position) ?? 0,
                keepOffset: false,
                updateStationInfo: true,
            });
        } else if (picker === Picker.Area) {
            if (measureObjectIsVertex(measure.hover)) {
                dispatch(areaActions.addPt(measure.hover.parameter, view));
            } else {
                dispatch(areaActions.addPt(position, view));
            }
        } else if (picker === Picker.PointLine) {
            addPointToPointLine({
                position: result.position,
                position2d: [evt.nativeEvent.offsetX, evt.nativeEvent.offsetY],
                objectId: result.objectId,
                measureEntity: measure.hover ?? (await pickMeasureEntity(result)),
            });
        } else if (picker === Picker.HeightProfileEntity) {
            if (result.objectId === -1) {
                return;
            }

            dispatch(heightProfileActions.selectPoint({ id: result.objectId, pos: vec3.clone(position) }));
        } else if (picker === Picker.Pins) {
            // Does nothing since handling of clicking on the canvas is done on MarkerCreationMode.tsx for the pins picker. This case is added to avoid a confusing warning message.
        } else {
            console.warn("Picker not handled", picker);
        }
    };

    return handleCanvasPick;
}
